import React, { useEffect, useMemo, useState } from 'react';
import { Button, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import SelectDropdown from 'components/base/select-dropdown';
import CurrencyInput from 'components/base/currency-input';
import { DeleteIcon } from 'cokitchen-icons';
import useDashboardContext from 'contexts/dashboard.context';
import { BatchesType } from 'models/batches.model';
import { toast } from 'react-toastify';

interface DamagedBatchType {
  batch_no: string;
  quantityDamaged: number;
  id: string;
}
export type DefaultBatchType = {
  batch_no: number;
  id: string;
};

export type WastePayloadType = { note: string; batches: { batch_no: number; quantity: number }[] };
type ErrorMap = Record<string, string>;
type wasteFormProps = {
  ingredientDetails: { name: string; unitType: string };
  batches: BatchesType[];
  submitting: boolean;
  onSubmit: (data: WastePayloadType) => void;
  onCancel?: () => void;
  defaultBatch: DefaultBatchType;
  canWrite?: boolean;
};
const WasteForm = ({
  ingredientDetails,
  batches,
  submitting,
  onSubmit,
  onCancel,
  defaultBatch,
  canWrite = true
}: wasteFormProps) => {
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();

  const [errors, setErrors] = useState<ErrorMap>({});
  const [note, setNote] = useState('');
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [selectedBatches, setSelectedBatches] = useState<DamagedBatchType[]>([
    { batch_no: '', quantityDamaged: 0, id: '' }
  ]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to stock');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  useEffect(() => {
    if (batches.length > 0) {
      setSelectedBatches([
        {
          batch_no: `Batch ${defaultBatch.batch_no}`,
          quantityDamaged: 0,
          id: defaultBatch.id
        }
      ]);
    }
  }, []);

  const allBatchesOptions = useMemo(() => {
    if (!batches) return [];
    return batches.map((batch) => ({
      name: `Batch ${batch.batch_no}`,
      id: batch.id,
      quantity: batch.quantity
    }));
  }, [batches]);

  const addNewBatch = () => {
    if (!canWrite) return;
    if (allBatchesOptions.length === selectedBatches.length) {
      toast(<ToastMessage message='All batches have been added' type='error' />);
      return;
    }
    setSelectedBatches([...selectedBatches, { batch_no: '', quantityDamaged: 0, id: '' }]);
  };

  const updateDamagedItem = (index: number, updates: Partial<DamagedBatchType>) => {
    const updatedItems = [...selectedBatches];
    updatedItems[index] = {
      ...updatedItems[index],
      ...updates
    };
    setSelectedBatches(updatedItems);
  };

  const handleQuantityChange = (value: string, index: number) => {
    const batchKey = selectedBatches[index].batch_no;
    const numericValue = Number(value);

    if (isNaN(numericValue) || numericValue < 0) {
      setErrors((prev) => ({
        ...prev,
        [batchKey]: 'Must be a positive number'
      }));
      return;
    }

    const selectedBatch = allBatchesOptions.find((b) => b.name === batchKey);
    const maxQuantity = Number(selectedBatch?.quantity || 0);

    if (maxQuantity && numericValue > maxQuantity) {
      setErrors((prev) => ({
        ...prev,
        [batchKey]: `Exceeds maximum allowed (${maxQuantity})`
      }));
      return;
    }

    setInputValues((prev) => ({
      ...prev,
      [batchKey]: value
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[batchKey];
      return next;
    });

    updateDamagedItem(index, { quantityDamaged: numericValue });
  };

  const isFormValid = () => {
    if (selectedBatches.length === 0) return false;

    return selectedBatches.every((batch) => {
      return Boolean(batch.batch_no) && batch.quantityDamaged > 0 && !errors[batch.batch_no];
    });
  };

  const handleDeleteBatch = (index: number) => {
    const batchKey = selectedBatches[index].batch_no;

    const updatedBatches = selectedBatches.filter((_, i) => i !== index);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[batchKey];
      return next;
    });
    setInputValues((prev) => {
      const next = { ...prev };
      delete next[batchKey];
      return next;
    });
    setSelectedBatches(updatedBatches);
  };

  const filteredOptions = (index: number) => {
    const options = allBatchesOptions.filter(
      (option) =>
        !selectedBatches.some((selected, i) => selected.batch_no === option.name && i !== index)
    );
    return options;
  };

  const handleSubmit = () => {
    const wastageData = {
      note,
      batches: selectedBatches.map((batch) => {
        const fetchedBatch = batches.find((b) => b.id === batch.id);
        return {
          batch_no: fetchedBatch?.batch_no || 0,
          quantity: batch.quantityDamaged
        };
      })
    };
    onSubmit(wastageData);
  };

  const getMaxQuantity = (batch_no: string) => {
    const batch = allBatchesOptions.find((b) => b.name === batch_no);
    return batch ? batch.quantity : 0;
  };

  return (
    <div className='ml-2 md:ml-8 edit-stock'>
      <h4 className='text-[24px] font-semibold text-[#2E3031] mb-11'>
        Record Damage for {ingredientDetails.name}
      </h4>
      <div className='bg-[#F7F7F8] w-full max-w-[678px] h-auto p-1 flex rounded-md justify-center items-center'>
        <div className='bg-white w-full max-w-[670px] h-auto rounded-md pt-6'>
          <h4 className='font-semibold text-[20px] pl-7 mb-1'>Update {ingredientDetails.name}</h4>
          <p className='text-[#6F7376] text-sm mb-8 pl-7'>Did any part of this item get damaged?</p>

          <div className='px-7 pb-6 space-y-6'>
            <h3 className='font-bold text-lg'>{ingredientDetails.name}</h3>

            {selectedBatches.map(({ batch_no }, index) => (
              <div key={index} className='flex gap-2 items-center'>
                <div className='grid grid-cols-2 gap-4'>
                  {filteredOptions(index).length > 0 && (
                    <div className='w-full'>
                      <SelectDropdown
                        label='Batch'
                        placeholder='Select batch'
                        value={batch_no}
                        setValue={(val) => {
                          updateDamagedItem(index, { batch_no: val.name, id: val.id });
                        }}
                        options={filteredOptions(index)}
                      />
                    </div>
                  )}

                  <CurrencyInput
                    label='Quantity damaged'
                    disabled={!batch_no || !canWrite}
                    placeholder='Quantity damaged'
                    name={`quantity_damaged_${index}`}
                    hasUnit
                    unitValue={ingredientDetails.unitType}
                    disableUnit
                    hideCurrency
                    explicitRender
                    value={inputValues[batch_no] || ''}
                    onWheel={(e) => e.currentTarget.blur()}
                    error={errors[batch_no]}
                    onChange={(value: string) => handleQuantityChange(value, index)}
                  />
                  {getMaxQuantity(batch_no) && (
                    <div className='mt-[-12px]'>
                      <p className='text-blue-5 text-sm'>
                        Maximum Quantity: {getMaxQuantity(batch_no)}
                      </p>
                    </div>
                  )}
                </div>
                {canWrite && selectedBatches.length > 1 && (
                  <div
                    className='cursor-pointer text-red-5'
                    onClick={() => handleDeleteBatch(index)}>
                    <DeleteIcon />
                  </div>
                )}
              </div>
            ))}
            <div className='flex justify-end'>
              {canWrite && allBatchesOptions.length !== selectedBatches.length && (
                <button
                  onClick={addNewBatch}
                  className='text-blue-5 font-medium text-sm hover:text-blue-600 transition-colors'>
                  + Add New Batch
                </button>
              )}
            </div>
            <div className='w-full'>
              <Textarea
                label='Add Note'
                name='note'
                value={note}
                disabled={!canWrite}
                onChange={(e) => setNote(e.target.value)}
                className='w-full   bg-[#F4F5F5]  h-[94px]'
              />
            </div>
          </div>

          <div className='flex justify-end gap-6 py-7 px-4 md:px-7 items-center border-t-2 border-t-[#F7F7F8]'>
            <Button variant='link' color='gray' small onClick={onCancel}>
              Back
            </Button>
            {canWrite && (
              <Button
                variant='block'
                color='primary'
                small
                className='w-[153px]'
                disabled={!isFormValid()}
                loading={submitting}
                onClick={handleSubmit}>
                Confirm
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WasteForm;
